import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { useLanguage } from '@/contexts/AppContext';
import svgPaths from '../imports/svg-v8h754cli1';

interface VerificationCodePageProps {
  onBack: () => void;
  onVerify: (code: string) => void;
  onResend?: () => Promise<{ success: boolean }>;
  deliveryChannel?: 'sms' | 'whatsapp';
  phoneNumber?: string;
  isVerifying?: boolean;
  verifyError?: string | null;
  attemptsRemaining?: number | null;
  isSendingOtp?: boolean;
  sendOtpError?: string | null;
}

export function VerificationCodePage({
  onBack,
  onVerify,
  onResend,
  deliveryChannel = 'whatsapp',
  phoneNumber = '+52 5512345678',
  isVerifying = false,
  verifyError = null,
  attemptsRemaining = null,
  isSendingOtp = false,
  sendOtpError = null,
}: VerificationCodePageProps) {
  const { language } = useLanguage();
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes in seconds
  const [isExpired, setIsExpired] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft <= 0) {
      setIsExpired(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Handle input change
  const handleInputChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...code];
    
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }
    
    setCode(newCode);
    
    // Focus the next empty input or the last one
    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  // Check if code is complete
  const isCodeComplete = code.every(digit => digit !== '');

  // Handle submit
  const handleSubmit = () => {
    if (isCodeComplete && !isExpired) {
      onVerify(code.join(''));
    }
  };

  // Handle resend code
  const handleResend = async () => {
    // API를 통한 OTP 재발송
    if (onResend) {
      const result = await onResend();
      // 성공 시에만 상태 리셋
      if (result.success) {
        setCode(['', '', '', '', '', '']);
        setTimeLeft(180);
        setIsExpired(false);
        inputRefs.current[0]?.focus();
      }
    }
  };

  const translations = {
    ko: {
      title: '인증 코드를 입력하세요',
      subtitle: deliveryChannel === 'sms' ? 'SMS로 전송된' : 'WhatsApp으로 전송된',
      description: '6자리 인증 코드를 입력하세요',
      timer: '코드 유효 시간',
      expired: '코드가 만료되었습니다',
      resend: '코드 재전송',
      verify: '인증하기',
      back: '뒤로'
    },
    es: {
      title: 'Ingresa tu código',
      subtitle: deliveryChannel === 'sms' ? 'Enviado por SMS a' : 'Enviado por WhatsApp a',
      description: 'Introduce el código de 6 dígitos',
      timer: 'Tu código vence en',
      expired: 'Código expirado',
      resend: 'Reenviar código',
      verify: 'Verificar',
      back: 'Atrás'
    },
    en: {
      title: 'Enter your code',
      subtitle: deliveryChannel === 'sms' ? 'Sent by SMS to' : 'Sent via WhatsApp to',
      description: 'Enter the 6-digit verification code',
      timer: 'Code expires in',
      expired: 'Code expired',
      resend: 'Resend code',
      verify: 'Verify',
      back: 'Back'
    }
  };

  const t = translations[language];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="bg-primary relative w-full h-[50px]">
        <button
          onClick={onBack}
          className="absolute h-[21px] left-[20px] top-[15px] w-[12px] hover:opacity-80 transition-opacity z-10"
        >
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 12 21">
            <path d={svgPaths.p27596280} fill="white" />
          </svg>
        </button>
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="font-['Noto_Sans:Bold',_'Noto_Sans_KR:Bold',_sans-serif] font-bold text-[16px] text-white tracking-[-0.32px]" style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}>{t.title}</p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center px-[clamp(16px,5vw,24px)] pt-[clamp(24px,6vw,40px)] pb-8">
        {/* Subtitle */}
        <div className="text-center mb-[clamp(24px,6vw,32px)] animate-fadeIn">
          <p className="text-[clamp(13px,3.2vw,14px)] text-foreground mb-2">
            {t.subtitle}
          </p>
          <p className="text-[clamp(15px,3.8vw,16px)] font-bold text-primary">
            {phoneNumber}
          </p>
        </div>

        {/* Code Input Boxes */}
        <div className="flex gap-[clamp(8px,2vw,12px)] mb-[clamp(20px,5vw,28px)] animate-fadeIn" style={{ animationDelay: '0.1s' }} onPaste={handlePaste}>
          {code.map((digit, index) => (
            <div key={index} className="flex-shrink-0">
              <input
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                autoFocus={index === 0}
                className={`w-[clamp(40px,10vw,56px)] h-[clamp(40px,10vw,56px)] text-center text-[clamp(20px,5vw,26px)] font-bold rounded-[3px] border-2 transition-all duration-200 outline-none
                  ${digit
                    ? 'border-primary border-[3px] bg-primary/5 text-foreground shadow-sm shadow-primary/20 scale-105'
                    : 'border-border bg-background text-foreground'
                  }
                  focus:border-primary focus:ring-2 focus:ring-primary/20 focus:bg-primary/5 focus:scale-105
                  hover:border-primary/50 hover:scale-[1.02]
                  active:scale-95
                `}
              />
            </div>
          ))}
        </div>

        {/* Description */}
        <p className="text-[clamp(12px,3vw,13px)] text-muted-foreground text-center mb-[clamp(24px,6vw,32px)] max-w-[min(90%,320px)] px-4 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
          {t.description}
        </p>

        {/* Timer */}
        <div className="mb-[clamp(32px,8vw,48px)] animate-fadeIn" style={{ animationDelay: '0.3s' }}>
          {!isExpired ? (
            <div className="flex items-center gap-[clamp(6px,1.5vw,8px)] px-[clamp(12px,3vw,16px)] py-[clamp(8px,2vw,10px)] rounded-full bg-muted shadow-sm">
              <svg className="w-[clamp(18px,4.5vw,20px)] h-[clamp(18px,4.5vw,20px)] text-secondary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-[clamp(12px,3vw,14px)] text-foreground whitespace-nowrap">
                {t.timer}:{' '}
              </span>
              <span className="text-[clamp(14px,3.5vw,16px)] font-bold text-secondary tabular-nums">
                {formatTime(timeLeft)}
              </span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <p className="text-[clamp(13px,3.2vw,14px)] text-warning font-medium">
                {t.expired}
              </p>
              <button
                onClick={handleResend}
                disabled={isSendingOtp}
                className={`text-[clamp(13px,3.2vw,14px)] font-medium underline transition-colors ${
                  isSendingOtp
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-primary hover:text-primary-hover active:scale-95'
                }`}
              >
                {isSendingOtp
                  ? language === 'ko'
                    ? '발송 중...'
                    : language === 'es'
                      ? 'Enviando...'
                      : 'Sending...'
                  : t.resend}
              </button>
            </div>
          )}
        </div>

        {/* 에러 메시지 */}
        {(verifyError || sendOtpError) && (
          <div className="w-full max-w-[min(90%,400px)] px-4 mb-4 animate-fadeIn">
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive text-center">{verifyError || sendOtpError}</p>
              {attemptsRemaining !== null && attemptsRemaining > 0 && (
                <p className="text-xs text-destructive/80 text-center mt-1">
                  {language === 'ko'
                    ? `남은 시도 횟수: ${attemptsRemaining}회`
                    : language === 'es'
                      ? `Intentos restantes: ${attemptsRemaining}`
                      : `Attempts remaining: ${attemptsRemaining}`}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Verify Button */}
        <div className="w-full max-w-[min(90%,400px)] px-4 animate-fadeIn" style={{ animationDelay: '0.4s' }}>
          <Button
            onClick={handleSubmit}
            disabled={!isCodeComplete || isExpired || isVerifying}
            className={`w-full h-[clamp(44px,11vw,52px)] rounded-[5px] text-[clamp(14px,3.5vw,16px)] font-bold transition-all duration-200
              ${isCodeComplete && !isExpired && !isVerifying
                ? 'bg-primary hover:bg-primary-hover text-white hover:shadow-lg hover:scale-[1.02] active:scale-95'
                : 'bg-border text-muted-foreground cursor-not-allowed'
              }
            `}
          >
            {isVerifying ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {language === 'ko' ? '확인 중...' : language === 'es' ? 'Verificando...' : 'Verifying...'}
              </span>
            ) : (
              t.verify
            )}
          </Button>
        </div>

        {/* Helper Text */}
        <p className="text-[clamp(11px,2.8vw,12px)] text-muted-foreground text-center mt-6 px-4 max-w-[min(90%,320px)] animate-fadeIn" style={{ animationDelay: '0.5s' }}>
          {language === 'ko' 
            ? '코드를 받지 못하셨나요? 스팸 폴더를 확인해주세요.' 
            : language === 'es'
            ? '¿No recibiste el código? Revisa tu carpeta de spam.'
            : 'Didn\'t receive the code? Check your spam folder.'}
        </p>
      </div>
    </div>
  );
}
